import React from "react";
import { cn } from "@/lib/utils";

/**
 * Alert — inline notice / status message primitive.
 *
 * Tones:  info (default) | success | warning | danger
 *
 * Composition:
 *   <Alert tone="warning" title="…">Body copy.</Alert>
 *
 * A11y: `role="status"` for info/success (polite), `role="alert"` for
 * warning/danger (assertive). Dismissible variant renders a close
 * button; when clicked, `onDismiss()` is called (consumer manages
 * mount).
 *
 * Guardrails: NO gradient stripe, NO large icon, NO shadow.
 */

const TONES = {
  info: {
    role: "status",
    box: "bg-[var(--ds-color-info-soft)] border-[var(--ds-color-info)] text-[var(--ds-color-info)]",
  },
  success: {
    role: "status",
    box: "bg-[var(--ds-color-success-soft)] border-[var(--ds-color-success)] text-[var(--ds-color-success)]",
  },
  warning: {
    role: "alert",
    box: "bg-[var(--ds-color-warning-soft)] border-[var(--ds-color-warning)] text-[var(--ds-color-warning)]",
  },
  danger: {
    role: "alert",
    box: "bg-[var(--ds-color-danger-soft)] border-[var(--ds-color-danger)] text-[var(--ds-color-danger)]",
  },
};

export function Alert({
  tone = "info",
  title,
  onDismiss,
  className,
  "data-testid": testId,
  children,
  ...rest
}) {
  const t = TONES[tone] || TONES.info;
  return (
    <div
      role={t.role}
      data-testid={testId}
      className={cn(
        "w-full rounded-[var(--ds-radius-md)] border-l-4 border border-l-current",
        "px-4 py-3",
        t.box,
        className
      )}
      {...rest}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title && (
            <div className="text-[14px] font-semibold leading-snug">
              {title}
            </div>
          )}
          {children && (
            <div className={cn("text-[13px] text-[var(--ds-color-ink)] leading-snug", title && "mt-1")}>
              {children}
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={cn(
              "shrink-0 rounded-[var(--ds-radius-sm)] px-2 py-1 text-[12px]",
              "text-current hover:bg-black/5",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]"
            )}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

export default Alert;
